import { CSSProperties } from 'react'

import { TextClamp } from '../components/text/TextClamp'
import { ChartLabel } from '../plots/_shared/ChartLabel'
import { EditableText } from './EditableText'

interface EditableSlideTextProps {
  value: string
  field: string
  editable?: boolean
  onEdit?: (field: string, value: string) => void
  className?: string
  style?: CSSProperties
  tag?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'div'
  minLines?: number
  maxLines?: number
  lineHeight?: number | string
  tooltipOnTruncate?: boolean
  /**
   * D17: forwarded straight to `TextClamp`. Without this pass-through every slide that
   * reaches its clamp through this wrapper would lose the mode on the way down, and a
   * static export would still emit a Radix trigger that can never open.
   *
   * Optional and unset by default, so `TextClamp` applies its own `interactive` default
   * and a caller that does not pass it keeps byte-identical behavior.
   */
  renderMode?: 'interactive' | 'static'
  title?: string
  'data-testid'?: string
  /**
   * Plots plan LB: this text is a chart label. Read-only, it prints through the
   * shared truncation rule (`ChartLabel`): an ellipsis, and on an interactive
   * surface the (i) with the whole value as the clamp's next sibling.
   */
  chartLabel?: { hintIndex: number; fullText?: string | null; hintSize?: number }
}

export function EditableSlideText(props: EditableSlideTextProps) {
  const {
    value,
    field,
    editable,
    onEdit,
    className,
    style,
    tag = 'span',
    minLines = 1,
    maxLines = 2,
    lineHeight = '1.2',
    tooltipOnTruncate,
    renderMode,
    title,
    chartLabel,
  } = props
  const testId = props['data-testid']

  if (editable && onEdit) {
    return (
      <EditableText
        value={value}
        field={field}
        editable
        onEdit={onEdit}
        className={className}
        style={style}
        tag={tag}
      />
    )
  }

  if (chartLabel) {
    return (
      <ChartLabel
        as={tag}
        className={className}
        style={style}
        text={value}
        fullText={chartLabel.fullText}
        hintIndex={chartLabel.hintIndex}
        hintSize={chartLabel.hintSize}
        minLines={minLines}
        maxLines={maxLines}
        lineHeight={lineHeight}
        renderMode={renderMode}
        title={title}
        data-testid={testId}
      />
    )
  }

  return (
    <TextClamp
      as={tag}
      className={className}
      style={style}
      minLines={minLines}
      maxLines={maxLines}
      lineHeight={lineHeight}
      tooltipOnTruncate={tooltipOnTruncate}
      renderMode={renderMode}
      title={title}
      data-testid={testId}
    >
      {value}
    </TextClamp>
  )
}
